/**
 * Maps user info from localStorage to ChatwootProvider UserDetails format
 * Handles field name conversions and provides defaults
 */

export interface StoredUserInfo {
  user_id?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  org_id?: string;
  org_domain?: string;
  roles?: string[];
  permissions?: string[];
  [key: string]: any;
}

export interface ChatwootUserDetails {
  user_guid?: string;
  display_name?: string;
  name?: string;
  email?: string;
  mobile?: string;
  org_guid?: string;
}

/**
 * Convert stored user info to Chatwoot format
 * @param userInfo - User info from localStorage
 * @returns ChatwootUserDetails for ChatwootProvider
 */
export interface RybbitIdentity {
  userId: string;
  traits: Record<string, unknown>;
}

/**
 * Most accounts are created from a phone number alone and carry the literal
 * name "USER", which would make every row in Rybbit's users list read the
 * same. Those get the last four digits of the phone appended; anything that
 * looks like a real name is left untouched.
 */
export function rybbitDisplayName(
  name?: string | null,
  mobile?: string | null
): string | undefined {
  const base = (name || "").trim();
  if (!base) return undefined;
  if (!/^user$/i.test(base)) return base;

  const last4 = (mobile || "").replace(/\D/g, "").slice(-4);
  return last4 ? `${base} ${last4}` : base;
}

/**
 * Convert stored user info to a Rybbit identity.
 * `username` is Rybbit's primary display name in the users list and session
 * cards; the guid alone would render as an opaque id.
 */
export function mapUserToRybbit(userInfo: StoredUserInfo | null): RybbitIdentity | null {
  if (!userInfo?.user_id) return null;

  const username = rybbitDisplayName(
    userInfo.given_name || userInfo.name || userInfo.preferred_username,
    userInfo.mobile
  );

  return {
    userId: userInfo.user_id,
    traits: {
      ...(username ? { username } : {}),
      ...(userInfo.org_id ? { org: userInfo.org_id } : {}),
    },
  };
}

export function mapUserToChatwoot(userInfo: StoredUserInfo | null): ChatwootUserDetails | null {
  if (!userInfo) return null;

  return {
    user_guid: userInfo.user_id,           // Maps: user_id → user_guid
    display_name: userInfo.given_name || userInfo.name || userInfo.preferred_username,
    name: userInfo.name,                    // Keep as is
    email: userInfo.email,                  // Keep as is
    mobile: userInfo.mobile,                // Optional field
    org_guid: userInfo.org_id,             // Maps: org_id → org_guid
  };
}

/**
 * Get user details from localStorage
 * @returns Chatwoot-formatted user details or null
 */
export function getUserDetailsFromStorage(): ChatwootUserDetails | null {
  try {
    const storedUser = localStorage.getItem("user_info");
    if (!storedUser) return null;
    
    const userInfo = JSON.parse(storedUser) as StoredUserInfo;
    console.log('📦 Retrieved user from localStorage:', {
      user_id: userInfo.user_id,
      email: userInfo.email,
      name: userInfo.name,
      org_id: userInfo.org_id
    });
    
    return mapUserToChatwoot(userInfo);
  } catch (error) {
    console.error('❌ Failed to parse user from localStorage:', error);
    return null;
  }
}

/**
 * Clear all user-related data from storage (on logout)
 */
export function clearUserFromStorage(): void {
  console.log('🧹 Clearing user data from storage');
  localStorage.removeItem("user_info");
  localStorage.removeItem("access_token");
  localStorage.removeItem("id_token");
  localStorage.removeItem("login_time");
  
  // Also clear Chatwoot storage
  localStorage.removeItem("chatwoot_contact");
  localStorage.removeItem("chatwoot_source_id");
  localStorage.removeItem("chatwoot_pubsub_token");
}
